import React, { useEffect } from 'react'
import { QueryClient, focusManager } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'

/**
 * Query client with a localStorage-persisted subset of the cache.
 *
 * WHY: the whole cache died on every refresh (it's in-memory), so each reload
 * re-fired the full n8n-proxy chain — campaign_metrics, leads_cards for every
 * campaign, then re-transformed every lead (the "Company Logo constructed"
 * spam). Leads/campaigns change rarely, so persisting just those two query
 * families makes a refresh render from disk instantly and revalidate in the
 * background instead of blocking on a slow proxy round-trip.
 *
 * WHAT IS PERSISTED (allowlist): campaigns + leads-by-campaign. Fresh-but-
 * stale entries still serve instantly; TanStack refetches behind the UI.
 * NOT persisted: lead details (large, private), credits (must be accurate),
 * anything auth/session-shaped.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10,   // 10 minutes
      retry: (failureCount, error: any) => {
        const status = error?.status
        // Never retry timeouts (408) or 5xx — these are slow (~30s each, e.g. heavy
        // get_lead calls) and retrying them floods n8n with duplicate executions.
        if (status === 408 || (typeof status === 'number' && status >= 500)) return false
        if (status === 404) return false
        return failureCount < 2
      },
    },
  },
})

/** localStorage adapter for the async persister. */
const localStorageAdapter = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
}

/**
 * Only persist the heavy, rarely-changing, refresh-killing query families.
 * The persister calls this per query; returning false drops everything else.
 *
 * leads.detail is included so the ISR workspace / lead-detail page renders
 * from localStorage instantly on refresh instead of blocking on a get_lead
 * round-trip. The stale cache renders first; TanStack revalidates behind it
 * (see the onSuccess handler: every boot invalidates the persisted families,
 * never marks them fresh).
 *
 * `sellers` (seller profiles, get_lvl1_profile) is persisted WITHOUT the boot
 * invalidation — profiles only change on explicit enrichment, so the Sellers
 * grid must not re-fire one request per card on every reload. Users force a
 * refresh via the Refresh button on the Sellers tab (invalidates ['sellers']).
 */
const shouldPersistQuery = (queryKey: readonly unknown[]): boolean => {
  const root = queryKey[0]
  if (root === 'campaigns' || root === 'leads') return true
  // Persist lead-report availability too — it fires one fileExists per row.
  if (root === 'storage') return true
  if (root === 'sellers') return true
  return false
}

const persister = createAsyncStoragePersister({
  storage: localStorageAdapter,
  // Bumped v1 → v2: the ISR notes validator now accepts `interaction_summary`
  // and `magnet_sent` activity_type values (previously rejected, which left
  // leads with those notes cached as isrNotesParseError:true and showed a
  // false "Saving blocked — notes need recovery" banner). A persisted cache
  // key bump drops the stale, pre-fix detail entries so the transformer
  // re-runs with the corrected validator on next load.
  key: 'brainiacs-rq-cache-v2',
  throttleTime: 3000, // batch writes — don't hammer localStorage per keystroke
})

interface Props {
  children: React.ReactNode
}

export const QueryProvider: React.FC<Props> = ({ children }) => {
  // Restore focus tracking after the persist restore clears it during boot
  // (persistQueryClient restore can leave focusManager paused).
  useEffect(() => {
    focusManager.setFocused(true)
    return () => focusManager.setFocused(undefined)
  }, [])

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24h — older persisted entries are dropped
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.state.status === 'success' && shouldPersistQuery(query.queryKey),
        },
      }}
      onSuccess={async () => {
        // ALWAYS sync on boot — same behavior for reloads, new tabs, and
        // first opens. Persisted data renders instantly (that's the point of
        // the allowlist), then invalidation marks it stale and refetches the
        // mounted observers in the background.
        //
        // EXCEPTION — `sellers`: never invalidated on boot. Seller profiles
        // only change on explicit enrichment; re-firing get_lvl1_profile for
        // every card on each reload is exactly the "wait and wait again" the
        // persistence exists to prevent. The Sellers-tab Refresh button (and
        // the enrich flow) invalidates ['sellers'] explicitly.
        //
        // Earlier versions only invalidated on browser reloads; other boots
        // RESET dataUpdatedAt so hours-old persisted data counted as fresh.
        // That hid real campaign_metrics / leads_cards state behind cache.
        // The reset was an anti-flood guard from the n8n era (boot refetch
        // storms hitting 25s webhook timeouts) — obsolete now that the eager
        // leads prefetch is gone and the Core redirect makes the boot set
        // ~3 lightweight calls, the same set a reload already invalidates.
        queryClient.getQueryCache().getAll().forEach(query => {
          if (!shouldPersistQuery(query.queryKey)) return;
          if (query.queryKey[0] === 'sellers') return;
          query.invalidate();
        })
      }}
    >
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  )
}
